import type { FC } from 'react';
import { Show } from '@components/Show';
import { mdiCreationOutline, mdiPaperclip } from '@mdi/js';
import { getFeatureFlag } from '@root/helpers/feature-flags';
import { getDataTestId } from '@root/src/utils/element-testid-selectors';
import { useDocumentLocation } from '@src/hooks';
import clsx from 'clsx';

import { useBlockCreator } from '../../context/context';
import { StepperCard } from '../StepperCard';
import { StepView } from '../StepView';

export const StepStart: FC = () => {
  const { isEndorsementView } = useDocumentLocation();
  const { goToStep } = useBlockCreator();
  const createBlockDescription = isEndorsementView ? 'Create a new summary block' : 'Create a new block or clause';
  const hasInlineAttachmentsFeature = getFeatureFlag('Attachments');

  return (
    <StepView stepId="start">
      <div
        className={clsx(
          'm-auto flex h-full w-fit items-center justify-center gap-x-10 gap-y-5',
          isEndorsementView ? 'flex-row' : 'flex-wrap',
        )}
      >
        <Show when={!isEndorsementView}>
          <StepperCard
            cta="Select block"
            svgName="blocks"
            description="Select a block from the library"
            onClick={() => goToStep('library-block')}
            data-testid={getDataTestId('block.addBlockFromLibrary').toString()}
          />
          <StepperCard
            cta="Select clause"
            svgName="clauses"
            description="Select a clause from the library"
            onClick={() => goToStep('library-clause')}
            data-testid={getDataTestId('block.addClauseFromLibrary').toString()}
          />
        </Show>
        <Show when={hasInlineAttachmentsFeature && !isEndorsementView}>
          <div className="basis-full" />
        </Show>
        <StepperCard
          cta="Create block"
          icon={mdiCreationOutline}
          description={createBlockDescription}
          onClick={() => goToStep('block')}
          data-testid={getDataTestId('block.addCustom').toString()}
          className={clsx(isEndorsementView && 'w-full [&>div]:items-center')}
        />
        <Show when={hasInlineAttachmentsFeature}>
          <StepperCard
            cta="Add attachment"
            icon={mdiPaperclip}
            description="Add an inline PDF attachment"
            onClick={() => goToStep('inline-attachment')}
            data-testid={getDataTestId('block.addInlineAttachment').toString()}
            className={clsx(isEndorsementView && 'w-full [&>div]:items-center')}
          />
        </Show>
      </div>
    </StepView>
  );
};
